import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExpandMore } from "@mui/icons-material";
import { useCompany } from "../../../contexts/CompanyContext";
import { Icons } from "../../shared/Icons";
import { SidebarButton } from "./SidebarButton";

const BASE_REPORT_PATHS = [
  "/daily-cash",
  "/monthly-cash",
  "/expenses",
  "/projections",
  "/birthdays",
  "/follow-up-clients",
  "/pending-payments",
] as const;

const CAR_ONLY_REPORT_PATHS = ["/cars-throughput", "/employee"] as const;

interface SidebarReportsGroupProps {
  isMinimized: boolean;
  isMobileMenuOpen: boolean;
  primaryColor: string;
  primaryColorHover: string;
  getBasePath: () => string;
  onNavigate: () => void;
}

export const SidebarReportsGroup: React.FC<SidebarReportsGroupProps> = ({
  isMinimized,
  isMobileMenuOpen,
  primaryColor,
  primaryColorHover,
  getBasePath,
  onNavigate,
}) => {
  const location = useLocation();
  const { company } = useCompany();
  const isCarCompany = company?.category === "car";
  const base = getBasePath();

  const reportPaths = isCarCompany
    ? [...BASE_REPORT_PATHS, ...CAR_ONLY_REPORT_PATHS]
    : [...BASE_REPORT_PATHS];

  const isChildActive = reportPaths.some(
    (p) => location.pathname === `${base}${p}`
  );

  const [expanded, setExpanded] = useState(() => isChildActive);

  useEffect(() => {
    if (isChildActive) {
      setExpanded(true);
    }
  }, [isChildActive]);

  const compact = isMinimized && !isMobileMenuOpen;

  if (compact) {
    return (
      <div className="space-y-2">
        <SidebarButton
          icon={<Icons.Receipt sx={{ fontSize: 20 }} />}
          label="Caixa do Dia"
          path="/daily-cash"
          isMinimized
          primaryColor={primaryColor}
          primaryColorHover={primaryColorHover}
          getBasePath={getBasePath}
          onNavigate={onNavigate}
        />
        <SidebarButton
          icon={<Icons.CalendarMonth sx={{ fontSize: 20 }} />}
          label="Caixa do Mês"
          path="/monthly-cash"
          isMinimized
          primaryColor={primaryColor}
          primaryColorHover={primaryColorHover}
          getBasePath={getBasePath}
          onNavigate={onNavigate}
        />
        <SidebarButton
          icon={<Icons.Payment sx={{ fontSize: 20 }} />}
          label="Pendentes"
          path="/pending-payments"
          isMinimized
          primaryColor={primaryColor}
          primaryColorHover={primaryColorHover}
          getBasePath={getBasePath}
          onNavigate={onNavigate}
        />
        <SidebarButton
          icon={<Icons.TrendingDown sx={{ fontSize: 20 }} />}
          label="Despesas"
          path="/expenses"
          isMinimized
          primaryColor={primaryColor}
          primaryColorHover={primaryColorHover}
          getBasePath={getBasePath}
          onNavigate={onNavigate}
        />
        <SidebarButton
          icon={<Icons.Chart sx={{ fontSize: 20 }} />}
          label="Projeções"
          path="/projections"
          isMinimized
          primaryColor={primaryColor}
          primaryColorHover={primaryColorHover}
          getBasePath={getBasePath}
          onNavigate={onNavigate}
        />
        <SidebarButton
          icon={<Icons.Cake sx={{ fontSize: 20 }} />}
          label="Aniversários"
          path="/birthdays"
          isMinimized
          primaryColor={primaryColor}
          primaryColorHover={primaryColorHover}
          getBasePath={getBasePath}
          onNavigate={onNavigate}
        />
        <SidebarButton
          icon={<Icons.Phone sx={{ fontSize: 20 }} />}
          label="Retorno"
          path="/follow-up-clients"
          isMinimized
          primaryColor={primaryColor}
          primaryColorHover={primaryColorHover}
          getBasePath={getBasePath}
          onNavigate={onNavigate}
        />
        {isCarCompany && (
          <>
            <SidebarButton
              icon={<Icons.Car sx={{ fontSize: 20 }} />}
              label="Produção"
              path="/cars-throughput"
              isMinimized
              primaryColor={primaryColor}
              primaryColorHover={primaryColorHover}
              getBasePath={getBasePath}
              onNavigate={onNavigate}
            />
            <SidebarButton
              icon={<Icons.Assignment sx={{ fontSize: 20 }} />}
              label="Funcionário"
              path="/employee"
              isMinimized
              primaryColor={primaryColor}
              primaryColorHover={primaryColorHover}
              getBasePath={getBasePath}
              onNavigate={onNavigate}
            />
          </>
        )}
      </div>
    );
  }

  const parentActiveClass = isChildActive
    ? "bg-white text-gray-900 shadow-md dark:bg-slate-700 dark:text-white"
    : "text-gray-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800";

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base ${parentActiveClass}`}
        style={
          isChildActive
            ? { boxShadow: `inset 3px 0 0 ${primaryColor}` }
            : undefined
        }
        aria-expanded={expanded}
      >
        <span className="flex items-center justify-center [&>.MuiSvgIcon-root]:!text-[1.25rem] sm:[&>.MuiSvgIcon-root]:!text-[1.125rem]">
          <Icons.Chart sx={{ fontSize: 20 }} />
        </span>
        <span className="truncate flex-1 text-left">Relatórios</span>
        <ExpandMore
          sx={{
            fontSize: 22,
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {expanded && (
        <div className="pl-2 border-l-2 border-gray-200 dark:border-slate-600 ml-3 space-y-1">
          <SidebarButton
            icon={<Icons.Receipt sx={{ fontSize: 18 }} />}
            label="Caixa do Dia"
            path="/daily-cash"
            isMinimized={false}
            nested
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            getBasePath={getBasePath}
            onNavigate={onNavigate}
          />
          <SidebarButton
            icon={<Icons.CalendarMonth sx={{ fontSize: 18 }} />}
            label="Caixa do Mês"
            path="/monthly-cash"
            isMinimized={false}
            nested
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            getBasePath={getBasePath}
            onNavigate={onNavigate}
          />
          <SidebarButton
            icon={<Icons.Payment sx={{ fontSize: 18 }} />}
            label="Pendentes"
            path="/pending-payments"
            isMinimized={false}
            nested
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            getBasePath={getBasePath}
            onNavigate={onNavigate}
          />
          <SidebarButton
            icon={<Icons.TrendingDown sx={{ fontSize: 18 }} />}
            label="Despesas"
            path="/expenses"
            isMinimized={false}
            nested
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            getBasePath={getBasePath}
            onNavigate={onNavigate}
          />
          <SidebarButton
            icon={<Icons.Chart sx={{ fontSize: 18 }} />}
            label="Projeções"
            path="/projections"
            isMinimized={false}
            nested
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            getBasePath={getBasePath}
            onNavigate={onNavigate}
          />
          <SidebarButton
            icon={<Icons.Cake sx={{ fontSize: 18 }} />}
            label="Aniversários"
            path="/birthdays"
            isMinimized={false}
            nested
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            getBasePath={getBasePath}
            onNavigate={onNavigate}
          />
          <SidebarButton
            icon={<Icons.Phone sx={{ fontSize: 18 }} />}
            label="Retorno"
            path="/follow-up-clients"
            isMinimized={false}
            nested
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            getBasePath={getBasePath}
            onNavigate={onNavigate}
          />
          {isCarCompany && (
            <>
              <SidebarButton
                icon={<Icons.Car sx={{ fontSize: 18 }} />}
                label="Produção"
                path="/cars-throughput"
                isMinimized={false}
                nested
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={onNavigate}
              />
              <SidebarButton
                icon={<Icons.Assignment sx={{ fontSize: 18 }} />}
                label="Funcionário"
                path="/employee"
                isMinimized={false}
                nested
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={onNavigate}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};
